import JSZip from "jszip";
import axios from "../utils/axios";
import { EventKey, EventDataMap, BaseEvent } from "./EventKeys";
import { isDevelopmentEnvironment } from "../estada-config-release";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEND_THRESHOLD = 10;

// ---------------------------------------------------------------------------
// Internal session object structure (matches backend schema)
// ---------------------------------------------------------------------------

interface SessionObject {
  sessionId: string;
  userId: string;
  projectId: string;
  startTime: number; // UTC milliseconds
  events: BaseEvent[];
}

// ---------------------------------------------------------------------------
// EventLocker
//
// Accepts events via addEvent(), buffers them in memory, and sends them to
// the backend as a ZIP-wrapped JSON payload when SEND_THRESHOLD is reached,
// the session ends, or a day boundary is crossed.
// ---------------------------------------------------------------------------

export class EventLocker {
  private session: SessionObject | null = null;
  private buffer: BaseEvent[] = [];
  private dayCheckTimer: ReturnType<typeof setTimeout> | null = null;
  // -------------------------------------------------------------------------
  // Save in Backend Management
  // -------------------------------------------------------------------------
  deactivated(): boolean {
    //
    // return false; // for debugging
    //
    // if localhost, do not send events to backend
    if (isDevelopmentEnvironment()) {
      return true;
    }
    const notTrackingEmailEndings = ["@estada.de", "@estada.com", "@estada.net"];
    const userEmail = state.loggedInUser?.email;
    if (userEmail) {
      for (const ending of notTrackingEmailEndings) {
        if (userEmail.endsWith(ending)) {
          return true;
        }
      }
    }

    return false;
  }
  // -------------------------------------------------------------------------
  // Session management
  // -------------------------------------------------------------------------

  startSession(userId: string, projectId: string): void {
    // End any running session first (sends pending events)
    this.endSession();
    if (this.deactivated()) {
      this.session = null;
      return;
    }
    this.session = {
      sessionId: crypto.randomUUID(),
      userId,
      projectId,
      startTime: Date.now(),
      events: [],
    };
    this.buffer = [];

    this._scheduleDayCheck();
  }

  endSession(): void {
    this._cancelDayCheck();

    if (!this.session) {
      return;
    }

    if (this.buffer.length > 0) {
      this._send(this.buffer);
    }

    this.session = null;
    this.buffer = [];
  }

  flush(): void {
    this._flushBuffer();
  }

  // -------------------------------------------------------------------------
  // Event recording
  // -------------------------------------------------------------------------

  addEvent<K extends number>(eventKey: K, data: K extends EventKey ? EventDataMap[K] : Record<string, unknown>): void {
    if (!this.session) {
      return;
    }

    const event: BaseEvent = {
      ts: Math.floor((Date.now() - this.session.startTime) / 1000),
      e: eventKey,
      d: data,
    };

    this.buffer.push(event);

    if (this.buffer.length >= SEND_THRESHOLD) {
      this._flushBuffer();
    }
  }

  // -------------------------------------------------------------------------
  // Buffer flush (swap + fire-and-forget send)
  // -------------------------------------------------------------------------

  private _flushBuffer(): void {
    if (!this.session || this.buffer.length === 0) {
      return;
    }

    // Synchronously swap the buffer so new events go into the new array
    const sendingBuffer = this.buffer;
    this.buffer = [];

    // Fire-and-forget – no await
    this._send(sendingBuffer);
  }

  // -------------------------------------------------------------------------
  // ZIP creation and HTTP upload
  // -------------------------------------------------------------------------

  private async _send(events: BaseEvent[]): Promise<void> {
    if (!this.session) {
      return;
    }

    const sessionObject: SessionObject = {
      sessionId: this.session.sessionId,
      userId: this.session.userId,
      projectId: this.session.projectId,
      startTime: this.session.startTime,
      events,
    };

    try {
      const jsonString = JSON.stringify(sessionObject);

      const zip = new JSZip();
      zip.file("events.json", jsonString);
      const zipBlob = await zip.generateAsync({ type: "blob" });

      const fileName = `eventlog_${sessionObject.sessionId}_${Date.now()}.zip`;
      const formData = new FormData();
      formData.append("file", zipBlob, fileName);

      await axios.post("/api/eventlogs/upload", formData);
    } catch {
      // Events are discarded on error – no retry as per spec
    }
  }

  // -------------------------------------------------------------------------
  // Day-boundary detection
  // -------------------------------------------------------------------------

  private _scheduleDayCheck(): void {
    this._cancelDayCheck();

    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
    const msUntilMidnight = midnight.getTime() - now.getTime();

    this.dayCheckTimer = setTimeout(() => {
      this._onDayChange();
    }, msUntilMidnight);
  }

  private _cancelDayCheck(): void {
    if (this.dayCheckTimer !== null) {
      clearTimeout(this.dayCheckTimer);
      this.dayCheckTimer = null;
    }
  }

  private _onDayChange(): void {
    if (!this.session) {
      return;
    }

    // Capture current session metadata for the new session
    const userId = this.session.userId;
    const projectId = this.session.projectId;

    // Send pending events and close current session
    if (this.buffer.length > 0) {
      const sendingBuffer = this.buffer;
      this.buffer = [];
      this._send(sendingBuffer);
    }

    this.session = null;

    // Start a fresh session for the new day
    this.startSession(userId, projectId);
  }
}
